import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';

@Component({
  selector: 'app-launcher',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="launcher-container">
      <header class="header">
        <div class="logo">
          <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path></svg>
          Sorrus
        </div>
        <div class="user-info">
          <span class="guid-display">{{ guid }}</span>
          <button class="btn-logout" (click)="onLogout()">Beenden</button>
        </div>
      </header>

      <main class="content">
        <div class="welcome">
          <h1>Willkommen im <span class="text-gradient">Sorrus-Hub</span></h1>
          <p>Wählen Sie ein Modul aus, um zu starten.</p>
        </div>

        <div class="apps-grid">
          <!-- PDF Signer App -->
          <div class="app-card glass" (click)="launchApp('pdf-signer')">
            <div class="app-icon pdf">
              <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
            </div>
            <div class="app-details">
              <h3>PDF Signatur</h3>
              <p>Dokumente hochladen, Empfänger verwalten und digital signieren.</p>
            </div>
            <div class="app-action">Öffnen</div>
          </div>

          <!-- Encryption App -->
          <div class="app-card glass" (click)="launchApp('encryption')">
            <div class="app-icon enc">
              <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
            </div>
            <div class="app-details">
              <h3>Verschlüsselung</h3>
              <p>Sichere Dateiverschlüsselung mit militärischen Standards (AES-256).</p>
            </div>
            <div class="app-action">Öffnen</div>
          </div>

          <!-- WebP Converter App -->
          <div class="app-card glass" (click)="launchApp('webp-converter')">
            <div class="app-icon webp">
              <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>
            </div>
            <div class="app-details">
              <h3>WebP Konverter</h3>
              <p>Medien effizient in das WebP-Format konvertieren. Bulk-Aktionen möglich.</p>
            </div>
            <div class="app-action">Öffnen</div>
          </div>

          <!-- Video Converter App -->
          <div class="app-card glass" (click)="launchApp('video-converter')">
            <div class="app-icon video">
              <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><polygon points="23 7 16 12 23 17 23 7"></polygon><rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect></svg>
            </div>
            <div class="app-details">
              <h3>Video Konverter</h3>
              <p>WMV-Videos sauber und universell in MP4-Dateien umwandeln. Progressiv und ausfallsicher.</p>
            </div>
            <div class="app-action">Öffnen</div>
          </div>

          <!-- GIT Backup App -->
          <div class="app-card glass" (click)="launchApp('git')">
            <div class="app-icon git">
              <svg viewBox="0 0 24 24" width="32" height="32" stroke="currentColor" stroke-width="2" fill="none"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>
            </div>
            <div class="app-details">
              <h3>GIT Backup</h3>
              <p>Repositories und Branches sauber und verschlüsselt sichern als ZIP-Archiv.</p>
            </div>
            <div class="app-action">Öffnen</div>
          </div>
        </div>

        <!-- Admin Section: Pending Users -->
        <div class="admin-section" *ngIf="pendingUsers.length > 0">
          <div class="divider"><span>BENUTZERVERWALTUNG</span></div>
          <div class="pending-users-list glass">
            <div class="admin-header">
              <h3>Ausstehende Beitrittsanfragen</h3>
              <span class="count">{{ pendingUsers.length }}</span>
            </div>
            <div *ngFor="let user of pendingUsers" class="pending-user-card">
              <div class="user-main">
                <div class="avatar">{{ user.email.charAt(0).toUpperCase() }}</div>
                <div class="details">
                  <strong>{{ user.email }}</strong>
                  <small>Beitritt angefordert am {{ user.createdAt | date:'short' }}</small>
                </div>
              </div>
              <div class="actions">
                <button class="btn-reject" (click)="approveUser(user.id, false)">Ablehnen</button>
                <button class="btn-approve" (click)="approveUser(user.id, true)">Freischalten</button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <div class="glow-orb orb-1"></div>
      <div class="glow-orb orb-2"></div>
    </div>
  `,
  styles: [`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    :host {
      font-family: 'Inter', sans-serif;
      display: block;
      min-height: 100vh;
      background: #0f172a;
      color: #f8fafc;
      overflow-x: hidden;
    }

    .launcher-container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 2rem 3rem;
      position: relative;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 5rem;
      z-index: 20;
      position: relative;
    }

    .logo {
      font-size: 1.25rem; font-weight: 800; display: flex; align-items: center; gap: 0.75rem;
    }
    .logo svg { color: #38bdf8; }
    .logo span { color: #38bdf8; }

    .user-info { display: flex; align-items: center; gap: 1.5rem; }
    .guid-display { font-family: monospace; font-size: 0.8rem; color: #64748b; background: rgba(255,255,255,0.05); padding: 0.4rem 0.8rem; border-radius: 0.5rem; }
    .btn-logout { background: transparent; border: 1px solid rgba(255,255,255,0.1); color: #94a3b8; padding: 0.5rem 1rem; border-radius: 0.5rem; cursor: pointer; transition: all 0.2s; }
    .btn-logout:hover { color: #ef4444; border-color: #ef4444; background: rgba(239, 68, 68, 0.1); }

    .content { position: relative; z-index: 10; }
    
    .welcome { margin-bottom: 4rem; text-align: center; }
    .welcome h1 { font-size: 3.5rem; font-weight: 800; margin: 0 0 1rem; }
    .text-gradient { background: linear-gradient(to right, #38bdf8, #818cf8); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
    .welcome p { color: #94a3b8; font-size: 1.25rem; }

    .apps-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
      gap: 2rem;
    }

    .app-card {
      padding: 2.5rem;
      display: flex;
      flex-direction: column;
      gap: 1.5rem;
      cursor: pointer;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      position: relative;
    }

    .glass {
      background: rgba(30, 41, 59, 0.5);
      backdrop-filter: blur(16px);
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 2rem;
    }

    .app-card:hover:not(.disabled) {
      transform: translateY(-8px) scale(1.02);
      border-color: rgba(56, 189, 248, 0.3);
      box-shadow: 0 20px 40px -15px rgba(0, 0, 0, 0.4);
      background: rgba(30, 41, 59, 0.7);
    }

    .app-card.disabled {
      opacity: 0.6;
      cursor: default;
    }

    .app-icon {
      width: 64px; height: 64px; border-radius: 1.25rem;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.3s;
    }
    .app-icon.pdf { background: rgba(56, 189, 248, 0.1); color: #38bdf8; }
    .app-icon.enc { background: rgba(148, 163, 184, 0.1); color: #94a3b8; }
    .app-icon.webp { background: rgba(52, 211, 153, 0.1); color: #34d399; }
    .app-icon.video { background: rgba(168, 85, 247, 0.1); color: #a855f7; }
    .app-icon.git { background: rgba(240, 80, 50, 0.1); color: #f05032; }

    .app-card:hover .app-icon.pdf { background: #38bdf8; color: #0f172a; transform: rotate(-5deg); }
    .app-card:hover .app-icon.enc { background: #818cf8; color: #0f172a; transform: scale(1.1) rotate(-8deg); }
    .app-card:hover .app-icon.webp { background: #34d399; color: #0f172a; transform: rotate(5deg); }
    .app-card:hover .app-icon.video { background: #a855f7; color: #0f172a; transform: scale(1.05) rotate(-3deg); }
    .app-card:hover .app-icon.git { background: #f05032; color: #0f172a; transform: scale(1.05) rotate(4deg); }

    .app-details h3 { margin: 0 0 0.5rem; font-size: 1.5rem; font-weight: 700; }
    .app-details p { margin: 0; color: #94a3b8; line-height: 1.6; }

    .app-action {
      margin-top: 1rem; font-weight: 700; color: #38bdf8; text-transform: uppercase; font-size: 0.85rem; letter-spacing: 0.1em;
    }

    .glow-orb { position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.3; pointer-events: none; }
    .orb-1 { top: 0; left: -100px; width: 400px; height: 400px; background: #0ea5e9; }
    .orb-2 { bottom: 0; right: -100px; width: 500px; height: 500px; background: #4f46e5; }

    .admin-section { margin-top: 4rem; z-index: 10; position: relative; }
    .admin-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem; padding: 0 1rem; }
    .admin-header h3 { margin: 0; font-size: 1.25rem; font-weight: 700; }
    .admin-header .count { background: #ef4444; color: white; font-size: 0.75rem; font-weight: 700; padding: 0.1rem 0.5rem; border-radius: 999px; }

    .pending-users-list { padding: 1.5rem; }
    .pending-user-card { 
      display: flex; justify-content: space-between; align-items: center; 
      padding: 1rem; background: rgba(15, 23, 42, 0.4); border-radius: 1rem; margin-bottom: 0.75rem;
      border: 1px solid rgba(255,255,255,0.05);
    }
    .user-main { display: flex; align-items: center; gap: 1rem; }
    .avatar { width: 40px; height: 40px; border-radius: 50%; background: #475569; display: flex; align-items: center; justify-content: center; font-weight: 700; }
    .details { display: flex; flex-direction: column; }
    .details strong { font-size: 0.95rem; }
    .details small { font-size: 0.75rem; color: #64748b; }

    .actions { display: flex; gap: 0.75rem; }
    .btn-approve { background: #10b981; color: white; border: none; padding: 0.5rem 1rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
    .btn-approve:hover { background: #059669; transform: translateY(-1px); }
    .btn-reject { background: transparent; border: 1px solid #ef4444; color: #ef4444; padding: 0.5rem 1rem; border-radius: 0.5rem; font-weight: 600; cursor: pointer; transition: all 0.2s; }
    .btn-reject:hover { background: rgba(239, 68, 68, 0.1); }

    .divider { display: flex; align-items: center; margin: 3rem 0 2rem; color: #475569; font-size: 0.75rem; font-weight: 700; letter-spacing: 0.1em; }
    .divider::before, .divider::after { content: ""; flex: 1; height: 1px; background: rgba(255, 255, 255, 0.1); }
    .divider span { margin: 0 1.5rem; }
  `]
})
export class LauncherComponent implements OnInit {
  private authService = inject(AuthService);
  private userService = inject(UserService);
  private router = inject(Router);

  guid = this.authService.getGuid();
  pendingUsers: any[] = [];

  ngOnInit() {
    this.loadPendingUsers();
  }

  loadPendingUsers() {
    if (this.guid) {
      this.userService.getPendingUsers(this.guid).subscribe({
        next: (users) => this.pendingUsers = users,
        error: () => this.pendingUsers = []
      });
    }
  }

  approveUser(userId: string, approve: boolean) {
    const adminEmail = this.authService.getUserEmail();
    if (!adminEmail) {
      // If we only have the static GUID, we might still want to allow approval
      // but let's assume we need an admin email for now.
      // Actually, if it's the master guid, we can just use a dummy admin email or the master guid itself.
    }

    this.userService.approveUser(adminEmail || 'admin@master.local', userId, approve).subscribe(() => {
      this.loadPendingUsers();
    });
  }

  launchApp(appId: string) {
    if (appId === 'pdf-signer') {
      this.router.navigate(['/pdf-signer']);
    } else if (appId === 'encryption') {
      this.router.navigate(['/encryption']);
    } else if (appId === 'webp-converter') {
      this.router.navigate(['/webp-converter']);
    } else if (appId === 'video-converter') {
      this.router.navigate(['/video-converter']);
    } else if (appId === 'git') {
      this.router.navigate(['/git']);
    }
  }

  onLogout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}
